import React, { useState, useEffect, useCallback } from 'react';
import {
  TitleRow,
  ContainerRow,
  SearchInputStyle,
  ControlsDiV
} from '../styledComponents';
import CustomTypography from '../../CustomComponents/CustomTypography';
import { useSelector } from 'react-redux';
import PageTitle from '../../../Utils/PageTitle';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import {
  RESULTS_PER_PAGE,
  SEARCH_DELAY
} from '../../../Constants/StringConstants';
import { createBillerColumns, createBillerGroupColumns } from './tanstackColumnConfig';
import AddBillersForm from './AddBillersForm';
import AddBillerGroupForm from './AddBillerGroupForm';
import CustomSideDrawer from '../../CustomComponents/CustomSideDrawer';
import { StyledListTab } from './styledComponents';
import { StyledJobListTabPane } from '../JobList/styledComponents.js';

import { notification } from 'antd';
import { SearchOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDebounce } from '../../../Utils/Debounce';
import { AddButton } from '../styledComponents';
import { exportToExcel } from '../../../Utils/ExcelExport';
import { reInviteUser } from '../../../Apis/User';
import { disableOrEnableUser } from '../../../Apis/ClinicAdmin';
import {
  AddBiller,
  listAll,
  editBiller,
  listAllBillers,
  listAllBillerGroups,
  saveBillerGroup,
  editBillerGroup,
  changeBillerGroupStatus
} from '../../../Apis/Biller';

const Billers = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [searchString, setSearchString] = useState('');
  const [total, setTotal] = useState(0);
  const [admins, setAdmins] = useState([]);
  const [billerGroupsList, setBillerGroupsList] = useState([]);
  const [billersList, setBillersList] = useState([]);
  const [selectedTabKey, setSelectedTabKey] = useState('biller');
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filterKey, setFilterKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('');
  const [facilityIds, setFacilityIds] = useState([]);
  const [facilities, setFacilities] = useState();
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const userData = useSelector(state => state.userReducer);
  const notClinicAdmin =
    userData.userType !== 'clinicAdmin' && userData.userType !== 'admin';

  const fetchAll = () => {
    setLoading(true);
    if (selectedTabKey === 'biller') {
      listAll()
        .then(response => {
          setLoading(false);
          if (response.data.success) {
            setTotal(response.data.size);
            setAdmins(response.data.result);
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
          }
        })
        .catch(() => setLoading(false));
    } else {
      listAllBillerGroups()
        .then(response => {
          setLoading(false);
          if (response.data.success) {
            setTotal(response.data.size);
            setBillerGroupsList(response.data.result);
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
          }
        })
        .catch(() => setLoading(false));
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [selectedTabKey]);

  // Keep billersList in sync for the group form's biller multi-select
  useEffect(() => {
    listAllBillers()
      .then(response => {
        if (response.data.success) {
          setBillersList(response.data.result);
        }
      })
      .catch(() => {});
  }, [admins]);

  const submitHandler = values => {
    setSubmitLoading(true);
    const payload = values;
    values.phoneNumber.number = values.phoneNumber.number.replace(
      values.phoneNumber.countryCode,
      ''
    );
    if (selectedItem) {
      payload.id = selectedItem.id;
      editBiller(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Updated',
              description: 'Biller details updated.'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    } else {
      AddBiller(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Added',
              description: 'Biller added successfully'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    }
  };

  const submitGroupHandler = values => {
    setSubmitLoading(true);
    const payload = { ...values };

    if (selectedItem) {
      payload.id = selectedItem.id;
      editBillerGroup(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Updated',
              description: 'Biller group updated.'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    } else {
      saveBillerGroup(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Created',
              description: 'Biller group created successfully.'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    }
  };

  // const deactivateGroup = id => {
  //   deactivateBillerGroup(id)
  //     .then(response => {
  //       if (response.data.success) {
  //         notification['success']({
  //           message: 'Deactivated',
  //           description: response.data.message
  //         });
  //         fetchAll();
  //       } else {
  //         notification['error']({
  //           message: 'Failed',
  //           description: response.data.message
  //         });
  //       }
  //     })
  //     .catch(err => console.log(err));
  // };

  // data = { active, reassignTo?, updateExistingJobs? } (TRPA-8544)
  const updateGroupStatus = (id, data, onSuccess, onError) => {
    changeBillerGroupStatus(id, data, userData.sessionId)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: data.active ? 'Activated' : 'Deactivated',
            description:
              response.data.message ||
              (data.active ? 'Biller group activated.' : 'Biller group deactivated.')
          });
          fetchAll();
          if (onSuccess) onSuccess();
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
          if (onError) onError();
        }
      })
      .catch(() => {
        notification['error']({ message: 'Failed', description: 'Something went wrong.' });
        if (onError) onError();
      });
  };

  const reInvite = id => {
    reInviteUser(id)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: 'Success',
            description: response.data.message
          });
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const disableUser = id => {
    disableOrEnableUser(id)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: 'Success',
            description: response.data.message
          });
          fetchAll(0);
          setCurrentPage(0);
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const onChangeHandler = value => {
    setCurrentPage(0);
    setSearchString(value);
  };

  const debounceOnChange = useCallback(
    useDebounce(onChangeHandler, SEARCH_DELAY),
    []
  );

  const handleExport = () => {
    if (selectedTabKey === 'biller') {
      let filteredData = admins;
      if (searchString) {
        const searchLower = searchString.toLowerCase();
        filteredData = filteredData.filter(
          biller =>
            (biller.fullName &&
              biller.fullName.toLowerCase().includes(searchLower)) ||
            (biller.email &&
              biller.email.toLowerCase().includes(searchLower)) ||
            (biller.phoneNumber &&
              `${biller.phoneNumber.countryCode}${biller.phoneNumber.number}`.includes(
                searchLower
              ))
        );
      }
      const exportColumns = [
        { id: 'id', header: 'ID', accessorKey: 'id' },
        { id: 'fullName', header: 'Name', accessorKey: 'fullName' },
        { id: 'email', header: 'Email', accessorKey: 'email' },
        {
          id: 'phoneNumber',
          header: 'Phone Number',
          accessorKey: 'phoneNumber'
        }
      ];
      exportToExcel(filteredData, exportColumns, 'Billers');
    } else {
      let filteredData = billerGroupsList;
      if (searchString) {
        const searchLower = searchString.toLowerCase();
        filteredData = filteredData.filter(
          group =>
            group.groupName &&
            group.groupName.toLowerCase().includes(searchLower)
        );
      }
      const exportColumns = [
        { id: 'id', header: 'ID', accessorKey: 'id' },
        { id: 'groupName', header: 'Group Name', accessorKey: 'groupName' },
        { id: 'billers', header: 'Members', accessorKey: 'billers' },
        { id: 'createdAt', header: 'Created At', accessorKey: 'createdAt' }
      ];
      exportToExcel(filteredData, exportColumns, 'BillerGroups');
    }
  };

  const drawerTitle =
    selectedTabKey === 'biller'
      ? selectedItem
        ? 'Edit Biller'
        : 'Add Biller'
      : selectedItem
      ? 'Edit Group'
      : 'Add Group';

  return (
    <>
      <PageTitle title='Billers' />
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <CustomTypography color={theme['@text-color']}>
            Billers
          </CustomTypography>
          <ControlsDiV>
            <AddButton
              icon={<PlusOutlined />}
              type='primary'
              onClick={() => setAddDrawerVisible(true)}
            >
              {selectedTabKey === 'biller' ? 'Add Biller' : 'Add Group'}
            </AddButton>
            <SearchInputStyle
              placeholder='Search'
              prefix={<SearchOutlined />}
              value={searchString}
              onChange={event => {
                setSearchString(event.target.value);
                debounceOnChange(event.target.value);
              }}
            />
            <AddButton icon={<DownloadOutlined />} onClick={handleExport}>
              Export to Excel
            </AddButton>
            <AddButton
              onClick={() => {
                setSearchString('');
                setClearFiltersTrigger(t => t + 1);
              }}
            >
              Clear Filter
            </AddButton>
          </ControlsDiV>
        </TitleRow>

        <StyledListTab
          activeKey={selectedTabKey}
          onChange={key => {
            setSelectedTabKey(key);
            setSearchString('');
            setClearFiltersTrigger(t => t + 1);
          }}
        >
          <StyledJobListTabPane tab='Biller' key='biller'>
            <TanStackUserTable
              data={admins}
              columns={createBillerColumns(
                setSelectedItem,
                setAddDrawerVisible,
                disableUser,
                reInvite,
                notClinicAdmin
              )}
              loading={loading}
              searchValue={searchString}
              storageKey='users_page_billers'
              clearFiltersTrigger={clearFiltersTrigger}
            />
          </StyledJobListTabPane>

          <StyledJobListTabPane tab='Biller Group' key='billerGroup'>
            <TanStackUserTable
              data={billerGroupsList}
              columns={createBillerGroupColumns(
                setSelectedItem,
                setAddDrawerVisible,
                null,
                notClinicAdmin
              )}
              loading={loading}
              searchValue={searchString}
              storageKey='users_page_biller_groups'
              clearFiltersTrigger={clearFiltersTrigger}
            />
          </StyledJobListTabPane>
        </StyledListTab>

        <CustomSideDrawer
          title={drawerTitle}
          placement='right'
          closable={false}
          onClose={() => {
            setSelectedItem(null);
            setAddDrawerVisible(false);
          }}
          visible={addDrawerVisible}
          width={400}
          destroyOnClose={true}
        >
          {selectedTabKey === 'biller' ? (
            <AddBillersForm
              facilities={facilities}
              selectedItem={selectedItem}
              submitHandler={submitHandler}
              submitLoading={submitLoading}
            />
          ) : (
            <AddBillerGroupForm
              billers={billersList}
              selectedItem={selectedItem}
              submitHandler={submitGroupHandler}
              submitLoading={submitLoading}
              onStatusChange={updateGroupStatus}
            />
          )}
        </CustomSideDrawer>
      </ContainerRow>
    </>
  );
};

export default Billers;
